import { ToggleSwitch } from "@/components/ToggleSwitch/index";
import { ToastContext } from "@/context/toast/index.js";
import useModal from "@/hooks/use-modal/index";
import { settingApi } from "@/services/api/setting.js";
import { Fragment, useEffect, useState, useContext, useMemo, useRef } from "react";
import { ResetNotificationModal } from "../modals/reset-notification-modal.jsx";
import { ReactComponent as IconRefresh } from "@/assets/svgs/refresh.svg";
import { useBuildex } from "@/hooks/use-buildex";
import Button from "@/componentsDS/Button/Button";
import { ButtonSize, ButtonType, ButtonVariant } from "@/componentsDS/Button/Button.type.js";
import Modal from "@/componentsDS/Modal/Modal";
import CardConfigure from "@/componentsDS/Modal/CardConfigure.jsx";
import Icon from "@/componentsDS/Icon/Icon.jsx";
import InputDropdown from '@/componentsDS/InputDropdown'
import { useFeature } from "@/hooks/use-feature/index.js";
import { featureConstant } from "@/constants/configuration/feature.js";
import useConfig from "@/hooks/use-config/index.js";
import KeyHelper from "@/utils/key-helper.js";
import { useTradex } from "@/hooks/use-tradex/index";

export const NotificationSetting = ({ setLoading }) => {
	const dailyReportKeyWord = "Daily Reports";
	const { addToastMessage } = useContext(ToastContext);
	const {getUniqueDataElements, consumeItems, provideItems} = useConfig()

	const [dropdownSearch, setDropdownSearch] = useState('')
	const [selectedDataElements, setSelectedDataElements] = useState([])
	const [notificationSettings, setNotificationSettings] = useState([]);

	const dataElements = useMemo(() => getUniqueDataElements()??[], [consumeItems, provideItems])
	const isTradex = useTradex()
	const isEmailPreferenceEnabled = useFeature(featureConstant.EMAIL_PREFERENCE)

	const { visible: visibleDataElementNotification, show: showDataElementNotification, hide: hideDataElementNotification } = useModal()
	const { visible: isModalVisible, show: showModal, hide: hideModal } = useModal();

	const getNotificationApiCallRef = useRef(null)

	function formatter(response) {
		try {
			response.sort((a, b)=> {
				if (a.sequenceNo > b.sequenceNo) {
					return 1;
				}
				return -1;
			});
			const groups = response.reduce((prev, curr) => {
				if (!prev.find((g) => g.description === curr.group)) {
					prev.push({
						description: curr.group,
						checked: curr.notificationEnabled,
						subgroups: [],
					});
				}

				const group = prev.find((g) => g.description === curr.group);
				group.checked = curr.notificationEnabled || group.checked;

				if (
					!group.subgroups.find(
						(sg) => sg.description === curr.subGroup
					)
				) {
					group.subgroups.push({
						description: curr.subGroup,
						items: [],
					});
				}

				const subGroup = group.subgroups.find(
					(sg) => sg.description === curr.subGroup
				);

				// Items
				subGroup.items.push({
					id: curr.id,
					description: curr.description,
					checked: curr.notificationEnabled,
					sequenceNo: curr.sequenceNo,
				});
				return prev;
			}, []);
			return groups;
		} catch (error) {
			addToastMessage("Failed to format notification response", "error");
			return [];
		}
	}

	async function getNotifications(mounted = true) {

		// Prevent duplicate api calls
		const currentOptionsKey = KeyHelper.createKeyOfObject({ mounted })
		if(KeyHelper.isSameKey(currentOptionsKey, getNotificationApiCallRef.current)) return
		getNotificationApiCallRef.current = currentOptionsKey
		

		let [error, response] = await settingApi.getNotifications({
			setLoading,
			addToastMessage,
		});

		if (!mounted) {
			return;
		}
		if (error) {
			setNotificationSettings([]);
			return;
		}

		if (!isTradex) {
			response = response.filter(
				(item) =>
					item.subGroup !== "Package" &&
					item.subGroup !== "Record Transfer" &&
					item.subGroup !== "Maker Checker"
			);
		}

		if (!response.length) return [];

		const formattedData = formatter(response);
		setNotificationSettings(formattedData);

		const enabledElements = response[0]?.dataElementIds || [];
		const filteredElements = transformedDataElements.filter(element => enabledElements.includes(element.id))
		setSelectedDataElements(filteredElements)
	}

	async function updateNotifications(
		requestPayload,
		newState,
		mounted = true
	) {
		const [error] = await settingApi.updateNotifications(requestPayload, {
			setLoading,
			addToastMessage,
		});

		if (!mounted || error) {
			return;
		}
		setNotificationSettings(newState);
	}

	const updateDERequestPayload = () => {
		const dataElementIds = selectedDataElements.map(element => element.id)

		const buildItemPayload = (item) => ({
			emailNotificationId: item.id,
			userNotificationEnabled: item.checked,
			dataElementIds,
		});

		return notificationSettings
		  .flatMap(group =>
			group.subgroups.flatMap(subgroup =>
			  subgroup.items.map(buildItemPayload))
		  );
	  };

	useEffect(() => {
		let mounted = true;
		getNotifications(mounted);

		return () => {
			mounted = false;
		};
	}, [dataElements]);

	function cloneState(state) {
		return JSON.parse(JSON.stringify(state));
	}

	async function onSingleNotificationChanged(groupIdx, subgroupIdx, itemIdx) {
		const newState = cloneState(notificationSettings);
		const item = newState[groupIdx].subgroups[subgroupIdx].items[itemIdx];
		item.checked = !item.checked;

		const hasCheckedItem = newState[groupIdx].subgroups.reduce(
			(prev, curr) => {
				return prev || !!curr.items.find((itm) => itm.checked);
			},
			false
		);

		newState[groupIdx].checked = hasCheckedItem;

		const requestPayload = [
			{
				emailNotificationId: item.id,
				userNotificationEnabled: item.checked,
				dataElementIds: selectedDataElements.map(element => element.id)
			},
		];
		await updateNotifications(requestPayload, newState);
	}

	function getChangedNotifications(newState, groupIdx, targetValue) {
		const group = newState[groupIdx];
		group.checked = targetValue;

		const requestPayload = [];
		const dataElementIds = selectedDataElements.map(element => element.id)

		group.subgroups.forEach((subgroup) =>
			subgroup.items.forEach((item) => {
				if (item.checked === targetValue) {
					return;
				}
				item.checked = targetValue;
				requestPayload.push({
					emailNotificationId: item.id,
					userNotificationEnabled: targetValue,
					dataElementIds: dataElementIds
				});
			})
		);
		return requestPayload;
	}

	async function onMultiNotificationChanged(groupIdx) {
		const newState = cloneState(notificationSettings);
		const requestPayload = getChangedNotifications(
			newState,
			groupIdx,
			!newState[groupIdx].checked
		);
		await updateNotifications(requestPayload, newState);
	}

	function onResetBtnClicked() {
		showModal();
	}

	async function onResetModalConfirm() {
		const newState = cloneState(notificationSettings);
		const defaultValue = true;
		const requestPayload = newState.reduce(
			(prev, curr, currIdx, callerObj) => {
				const payload = getChangedNotifications(
					callerObj,
					currIdx,
					defaultValue
				);
				return [...prev, ...payload];
			},
			[]
		);

		await updateNotifications(requestPayload, newState);
	}

	const transformedDataElements = useMemo(() => {
		if (!Array.isArray(dataElements) || dataElements.length === 0) {
			return [];
		}
		return dataElements.map((item) => ({
			...item,
			id: item.id,
			label: item.name,
			uncategorized: 'uncategorized',
		}));
	}, [dataElements]);


	const dropdownSearchHandler = (e) => {
		if (e?.target) {
			setDropdownSearch(e.target.value)
		}
	}

	const handleConfirm = async () => {
		const requestPayload = updateDERequestPayload()
		await updateNotifications(requestPayload, notificationSettings, true)
		refreshAndHideElement()
	}

	const refreshAndHideElement = async () => {
		getNotifications()
		hideDataElementNotification();
	}

	return (
		<>
			<ResetNotificationModal
				isModalVisible={isModalVisible}
				hideModal={hideModal}
				confirmCallback={onResetModalConfirm}
			/>
			<div className="notification-container">
				<div
					data-testid="reset-default-notification-btn"
					className="reset-default-notification-btn"
					onClick={() => onResetBtnClicked()}
				>
					<div className="flex-box flex-align-center">
						<div className="DS__icon">

						<IconRefresh/>
						</div>
						<div className="reset-default-notification-btn-text font-weight-bold">
							Reset to default
						</div>
					</div>
				</div>

				<div className="notification-note">
					<span className="font-weight-bold text-color-purple">Note: </span>
					<span className="font-weight-normal">All changes are automatically saved.</span>
				</div>
				{notificationSettings?.map((group, groupIdx) => {
					return (
						<Fragment key={groupIdx}>
							<div
								data-testid="group-container"
								className="group-container"
							>
								<div className="flex-box mt-1 px-1">
									<div className="group-title font-weight-bold flex-10">
										{group.description}
									</div>
									<div className="flex-2">
										<ToggleSwitch
											data-testid="group-toggle"
											item={group}
											labelText={
												group.checked ? "On" : "Off"
											}
											callback={() =>
												onMultiNotificationChanged(
													groupIdx
												)
											}
										/>
									</div>
								</div>
								{group.description === dailyReportKeyWord && (
									<div
										data-testid="extra-description"
										className="font-size-s font-color-grey-80 mt-1 px-1 font-weight-normal"
									>
										Daily, 05:00 PM, Singapore &#40;SGT&#41;
									</div>
								)}

								<div className="group-content">
									{ group.description === "Email Notifications" && isEmailPreferenceEnabled && (
										<div className="flex-box mt-1 px-1">
											<div className="flex-10">
												<div className="subgroup-title font-weight-bold">
													<span>Data Element</span>
												</div>
												<div>
													<div className="font-color-grey-80 font-size-s line-height-2">
														{selectedDataElements.length > 0
															? `Receiving notifications for ${selectedDataElements.length} data element(s)`
															: 'You are not receiving notifications for any data elements'}
													</div>
												</div>
											</div>
											<div className="flex-2">
												<div className="flex-box flex-end DS__button--interactive-disabled">
													<Button
														text="Modify"
														type={ButtonType.GHOST}
														variant={ButtonVariant.STANDARD}
														size={ButtonSize.SMALL}
														onClick={() => showDataElementNotification()}
													/>
												</div>
											</div>
										</div>
									)}
									{group.subgroups?.map(
										(subgroup, subgroupIdx) => {
											return (
												<div key={subgroupIdx}>
													{!!subgroup.description && (
														<div className="subgroup-title font-weight-bold mt-1 px-1">
															{
																subgroup.description
															}
														</div>
													)}
												{subgroup.items?.map(
														(item, itemIdx) => {
															return (
																<div
																	key={
																		itemIdx
																	}
																	className={`notification-item-container flex-box p-1 ${
																		!!subgroup.description
																			? ""
																			: " notification-item-container-no-hover"
																	}`}
																>
																	<div className="notification-item font-weight-normal flex-10">
																		{
																			item.description
																		}
																	</div>
																	<div className="flex-2">
																		{!!subgroup.description ? (
																			<ToggleSwitch
																				data-testid="item-toggle"
																				item={
																					item
																				}
																				labelText={
																					item.checked
																						? "On"
																						: "Off"
																				}
																				callback={() =>
																					onSingleNotificationChanged(
																						groupIdx,
																						subgroupIdx,
																						itemIdx
																					)
																				}
																			/>
																		) : (
																			<div></div>
																		)}
																	</div>
																</div>
															);
														}
													)}
												</div>
											);
										}
									)}
								</div>
							</div>
							{groupIdx !== notificationSettings.length - 1 && (
								<hr className="separation-line" />
							)}
						</Fragment>
					);
				})}
			</div>
			<Modal dataTestId="data-element-modal" addClass="DS__button--interactive-disabled" visible={visibleDataElementNotification} variant='small' close={hideDataElementNotification} dismissClickOutsideModal={false}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left card-configure__title__left--background">
										<Icon name="notification-settings" size="large" style="outlined" data-testid="notification-settings-icon" />
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Email Notifications - Data Element
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="notification-modal">
								<div className="notification-modal__top">
									Receive notification for the following Data Elements.
								</div>
								<div className="notification-modal__bottom">
									<InputDropdown
										value={dropdownSearch}
										onChange={dropdownSearchHandler}
										name={'dataElementSearch'}
										dropdownOption={transformedDataElements}
										placeholder={"Select an option..."}
										onSelectDropdownItem={setSelectedDataElements}
										dropdownSelectedOption={selectedDataElements}
										multipleSelection={true}
									/>
								</div>
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Left>
							<Button
								text="Cancel"
								type={ButtonType.GHOST}
								variant={ButtonVariant.STANDARD}
								size={ButtonSize.SMALL}
								onClick={refreshAndHideElement}
							/>
						</CardConfigure.Footer.Left>
						<CardConfigure.Footer.Right>
							<Button
								text="Confirm"
								type={ButtonType.SOLID}
								variant={ButtonVariant.STANDARD}
								size={ButtonSize.SMALL}
								onClick={handleConfirm}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>
		</>
	);
};
